// frontend/src/app/components/projects/project-corpus/dialogs/source-picker.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { ImportSourceType } from '../../../../project-corpus/domain/types';

interface SourceOption {
  readonly value: ImportSourceType;
  readonly label: string;
}

const SOURCE_OPTIONS: readonly SourceOption[] = [
  { value: 'OPENALEX', label: 'OpenAlex' },
  { value: 'BIBTEX', label: 'BibTeX' },
  { value: 'PDF', label: 'PDFs' },
  { value: 'GROUP_CORPUS', label: 'Group corpus' },
];

/**
 * Segmented pill control letting the user pick which import source the
 * Add-papers wizard should render. The active pill is highlighted; the others
 * are muted.
 *
 * Responsive behaviour:
 *  - Mobile (default): pills sit in a horizontally scrollable row
 *    (`overflow-x-auto`) so they never wrap and stay tappable.
 *  - Desktop (`sm:`): pills lay out inline with no horizontal scroll.
 */
@Component({
  selector: 'app-source-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './source-picker.component.html',
  styleUrls: ['./source-picker.component.scss'],
})
export class SourcePickerComponent {
  readonly options = SOURCE_OPTIONS;

  private readonly _selected = signal<ImportSourceType>('OPENALEX');

  @Input()
  set selected(value: ImportSourceType) {
    this._selected.set(value);
  }
  get selected(): ImportSourceType {
    return this._selected();
  }

  @Output() change = new EventEmitter<ImportSourceType>();

  readonly currentSelected = this._selected.asReadonly();

  private static readonly DISABLED: ReadonlySet<ImportSourceType> = new Set();

  isDisabled(value: ImportSourceType): boolean {
    return SourcePickerComponent.DISABLED.has(value);
  }

  isActive(value: ImportSourceType): boolean {
    return this._selected() === value;
  }

  pick(value: ImportSourceType): void {
    if (this.isDisabled(value)) return;
    if (this._selected() === value) return;
    this._selected.set(value);
    this.change.emit(value);
  }
}
